<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCarUsersStore } from '@/stores/car-users'

import Modal                from '@/components/UTAModal.vue'
import UTACarUsersCreate    from '@/components/car/UTACarUsersCreate.vue'
import UTACarUser           from '@/components/car/UTACarUser.vue'

function changePage(cursor, dir) {

    const params = {
        'c.limit': 6, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    carUsersStore.readCarUsers({ car: props.uuid, params}).then(result => {
        if(result.array) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const translaterStore   = useTranslaterStore()
const carUsersStore     = useCarUsersStore()

const getTranslate      = computed(() => translaterStore.getTranslate)
const carUsers          = computed(() => carUsersStore.carUsers)
const carUsersProcess   = computed(() => carUsersStore.carUsersProcess)
const carUsersMsg       = computed(() => carUsersStore.carUsersMsg)
const carUsersCreateMsg = computed(() => carUsersStore.carUsersCreateMsg)
const carUsersUpdateMsg = computed(() => carUsersStore.carUsersUpdateMsg)
const carUsersDeleteMsg = computed(() => carUsersStore.carUsersDeleteMsg)

const inputSearch = ref('')
const pageNumber = ref(1)
const showCreateCarUsers = ref(false)

carUsersStore.readCarUsers({ car: props.uuid, params: { 'c.limit': 6 } })

onBeforeUnmount(() => {

    carUsersStore.setCarUsersMsg({})
    carUsersStore.setCarUsersCreateMsg({})
    carUsersStore.setCarUsersUpdateMsg({})
    carUsersStore.setCarUsersDeleteMsg({})
})

watch(carUsersCreateMsg, msg => {
    if('success' in msg && msg.success) {
        carUsersStore.readCarUsers({ car: props.uuid, params: { 'c.limit': 6 } })
    }
})

watch(carUsersUpdateMsg, msg => {
    if('success' in msg && msg.success) {
        carUsersStore.readCarUsers({ car: props.uuid, params: { 'c.limit': 6 } })
    }
})

watch(inputSearch, input => {
    input.length > 2 ? 
        carUsersStore.readCarUsers({ car: props.uuid, params: { 'c.limit': 6, 'c.q': inputSearch.value } }) :
        carUsersStore.readCarUsers({ car: props.uuid, params: { 'c.limit': 6 } })
})
</script>

<template>
    <div class="uta-car-users mb-4">

        <header class="page__header uta-header" role="heading" aria-level="1">
            <h2 class="uta-header__title">{{ getTranslate.CAR_USERS }}</h2>
            <div class="spinner-border uta-header__spinner" role="status" v-if="carUsersProcess">
                <span class="visually-hidden">Loading...</span>
            </div>
            <div class="uta-header__actions">
                <a 
                    href="#" 
                    class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon uta-header__action uta-header__action--create"
                    @click.prevent="showCreateCarUsers = true"
                >
                    <span class="uta-btn__content">
                        <span class="uta-btn__icon material-icons">settings</span>
                        <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_CTRL }}</span>
                    </span>
                </a>
            </div>
        </header>

        <div class="row mb-4 justify-content-between">
            <div class="col-12">
                <div class="uta-input__wrapper uta-search">
                    <input 
                        type="text" 
                        :placeholder="getTranslate.SEARCH + '...'" 
                        v-model="inputSearch"
                        @keyup.escape="inputSearch = ''"
                        class="form-control uta-input__wrapper-input uta-search__input" 
                    >
                    <template v-if="inputSearch.length > 2">
                        <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = ''">
                            cancel
                        </span>
                    </template>
                    <template v-else>
                        <span class="material-icons uta-input__wrapper-icon">
                            search
                        </span>
                    </template>
                </div>
            </div>
        </div>

        <div 
            v-if="'success' in carUsersMsg && !carUsers.array?.length"
            class="my-4 uta-alert uta-alert--danger" 
        >
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text"> 
                {{ getTranslate.ELinkNotFound }}
            </div>
        </div>

        <div 
            v-if="'success' in carUsersDeleteMsg && carUsersDeleteMsg.success"
            class="my-4 uta-alert uta-alert--success"
        >
            <span class="material-icons uta-alert__icon">check_circle_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[carUsersDeleteMsg.text] || carUsersDeleteMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="carUsersStore.setCarUsersDeleteMsg({})">
                <span class="material-icons">close</span>
            </button>
        </div>

        <div 
            v-if="'success' in carUsersUpdateMsg"
            class="my-4 uta-alert" 
            :class="{ 
                'uta-alert--success'    : carUsersUpdateMsg.success, 
                'uta-alert--danger'     : !carUsersUpdateMsg.success
            }"
        >
            <span class="material-icons uta-alert__icon">
                {{ carUsersUpdateMsg.success ? 'check_circle_outline' : 'error_outline' }}
            </span>
            <div class="uta-alert__text">
                {{ getTranslate[carUsersUpdateMsg.text] || carUsersUpdateMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="carUsersStore.setCarUsersUpdateMsg({})">
                <span class="material-icons">close</span>
            </button>
        </div>

        <template v-if="carUsers.array?.length">

            <div class="container-fluid">
                <div class="uta-users-groups-cars-groups__list uta-list">
                    <div class="uta-list__title d-none d-lg-block uta-users-groups-cars-groups__title">
                        <div class="row">
                            <div class="col-10">
                                <div class="row">
                                    <div class="col-5">{{ getTranslate.USER_NAME }}</div>
                                    <div class="col-7">{{ getTranslate.USER_EMAIL }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="pb-4 uta-list__content">
                        <div 
                            class="row align-items-center uta-list__item uta-users-groups-cars-groups__group" 
                            :class="{ 'uta-list__item--dark' : index % 2 }" 
                            v-for="(user, index) in carUsers.array" 
                            :key="index"
                        >
                            <UTACarUser :user="user" :uuid="props.uuid" :outerIndex="index" @update-attrs="pageNumber = 1" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="uta-cars__pagination" v-if="carUsers.c && (carUsers.c.isBefore || carUsers.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                        <div class="uta-pagination">
                            <button 
                                :disabled="carUsersProcess || !carUsers.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(carUsers.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="carUsersProcess || !carUsers.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(carUsers.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <transition name="bounce">
            <Modal 
                v-if="showCreateCarUsers"
                :header="getTranslate.CAR_USERS_CREATE"
                size="md"
                @closeModal="showCreateCarUsers = false"
            >
                <template v-slot:body>
                    <UTACarUsersCreate @closeModal="showCreateCarUsers = false" :uuid="props.uuid" />
                </template>
            </Modal>
        </transition>

    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/cars';
</style>